import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import debounce from 'lodash.debounce';

import { onEvent, supportsPassiveEventListener } from 'helpers/EventHelpers';

import css from 'styles/components/common/melodyCarousel.scss';

class MelodyCarousel extends Component {
  static contextTypes = {
    testId: PropTypes.func
  };

  state = {
    currentIndex: 0
  };

  componentDidMount() {
    const { handleResize, handleTouchMove, setItemData, props: { children } } = this;
    if (children.length > 0) {
      setItemData();
    }
    const opts = supportsPassiveEventListener() ? { passive: true } : false;
    onEvent(window, 'resize', handleResize, opts, this);
    onEvent(window, 'resize', handleTouchMove, opts, this);
  }

  setIndex = newIndex => {
    const {
      props: { snap },
      state: { pageCount }
    } = this;

    newIndex = Math.max(0, Math.min(newIndex, pageCount - 1));

    if (snap) {
      snap(this, newIndex);
    }

    this.setState({ currentIndex: newIndex });
  };

  setItemData = () => {
    const {
      props: { children },
      state: { currentIndex },
      itemsContainer
    } = this;
    const { firstChild: firstItem, lastChild: lastItem } = itemsContainer;
    const computed = window.getComputedStyle(firstItem);
    const itemsContainerWidth = itemsContainer.getBoundingClientRect().width;
    const itemWidth = firstItem.getBoundingClientRect().width + parseInt(computed.marginLeft, 10) + parseInt(computed.marginRight, 10) || 1;
    const visibleItems = Math.floor((itemsContainerWidth / itemWidth).toFixed(2)) || 1; // toFixed used because of rounding errors noticed between Chrome and Firefox
    const pageCount = Math.ceil(children.length / visibleItems);
    const pageWidth = Math.floor(visibleItems * itemWidth);
    const maxSlots = visibleItems * pageCount;
    const orphanCount = maxSlots - children.length;
    const maxIndex = pageCount - 1;
    const rightEdgeOfLastItem = lastItem.getBoundingClientRect().right;

    if (currentIndex > maxIndex) {
      this.setState({ currentIndex: maxIndex });
    }

    this.setState({ pageCount, pageWidth, orphanCount, itemsContainerWidth, itemWidth, rightEdgeOfLastItem, visibleItems });
  };

  handleResize = debounce(() => {
    this.reconfigure();
  }, 100);

  handleTouchMove = () => {
    // ios10 scrolling fix (i.e. preventDefault() on touchmove doesn't stop vertical scrolling when swiping per https://github.com/timruffles/ios-html5-drag-drop-shim/issues/77)
  };

  makeArrows = () => {
    const {
      props: { classes },
      state: { currentIndex, pageCount },
      context: { testId = f => f },
      setIndex
    } = this;
    return [
      <button
        type="button"
        key="prev"
        ref={el => this.prev = el}
        className={cn(css.prev, classes.prevButton, { [css.inactive]: currentIndex === 0 })}
        onClick={() => setIndex(currentIndex - 1)}
        data-test-id={testId('prevArrow')}>
        Previous
      </button>,
      <button
        type="button"
        key="next"
        ref={el => this.next = el}
        className={cn(css.next, classes.nextButton, { [css.inactive]: currentIndex >= pageCount - 1 })}
        onClick={() => setIndex(currentIndex + 1)}
        data-test-id={testId('nextArrow')}>
        Next
      </button>
    ];
  };

  makeDots = () => {
    const {
      props: { showDots },
      state: { currentIndex, pageCount },
      context: { testId = f => f },
      setIndex
    } = this;
    if (showDots && pageCount > 1) {
      return (
        <div className={css.paging}>
          {Array(pageCount).fill(undefined).map((_, index) => (
            <button
              type="button"
              key={index}
              className={cn({ [css.active]: index === currentIndex })}
              onClick={() => setIndex(index)}
              tabIndex={0}
              data-test-id={testId('carouselPageDot')}>
              {index + 1}
            </button>
          ))}
        </div>
      );
    }
  };

  reconfigure = () => {
    const {
      state: { currentIndex },
      setIndex, setItemData,
      itemsContainer,
      props: { children }
    } = this;

    if (!itemsContainer) {
      return null;
    }

    if (children.length > 0) {
      setItemData();
      setIndex(currentIndex);
    }
  };

  swipe = event => {
    const { itemsContainer, mCarousel, setIndex } = this;
    let xStart = null;
    let yStart = null;
    let xEnd = null;
    let yEnd = null;
    let xDiff = null;
    let yDiff = null;
    const leftStart = itemsContainer.offsetLeft || 0; // starting point in px
    const leftOrig = itemsContainer.style.left; // original left value (could be px, %, em, rem, furlong, mile, league?) so we can reset if needed
    const threshold = 50; // the minimum distance needed to call the function
    const distance = 15; // the minimum distance to determine if x or y swipe
    let direction;
    const opts = supportsPassiveEventListener() ? { passive: true } : false;

    const start = e => {
      xStart = e.touches[0].clientX;
      yStart = e.touches[0].clientY;
    };

    const move = e => {
      if (xStart === null || yStart === null) {
        return;
      }

      xEnd = e.touches[0].clientX;
      yEnd = e.touches[0].clientY;

      xDiff = xStart - xEnd;
      yDiff = yStart - yEnd;

      const leftDiff = leftStart - xDiff;

      if (!direction) {
        if (Math.abs(xDiff) > distance) {
          direction = 'x';
        } else if (Math.abs(yDiff) > distance) {
          direction = 'y';
        }
      }

      if (direction === 'x') {
        e.stopPropagation();
        itemsContainer.style.left = `${leftDiff}px`;
      }
    };

    const end = e => {
      // console.info('finished move');
      itemsContainer.classList.remove(css.swiping);

      if (direction === 'x') {
        // x movement greater than y movement so activate rotation
        const dir = xDiff > 0 ? 1 : -1;

        if (Math.abs(xDiff) >= threshold) {
          setIndex(this.state.currentIndex + dir);

        } else {
          restore(e);
        }
      } else {
        // restore original left position since y movement greater than x movement
        restore(e);
      }

      mCarousel.removeEventListener('touchmove', move);
      mCarousel.removeEventListener('touchend', end);
    };

    const restore = () => {
      itemsContainer.style.left = leftOrig;
    };

    start(event);

    itemsContainer.classList.add(css.swiping);

    onEvent(mCarousel, 'touchmove', move, opts, this);
    onEvent(mCarousel, 'touchend', end, opts, this);
  };

  render() {
    const { pageCount } = this;
    const { buttonsOutsideItemsViewport, children, classes, padScrollArea, showDots, title } = this.props;
    const outerClass = cn(
      { [css.padScrollArea]: padScrollArea && pageCount > 1, [css.showDots]: showDots },
      css.mCarousel
    );
    if (children.length > 0) {
      const itemsContainerMarkup = (
        <div ref={el => this.itemsContainer = el} className={cn(css.items, classes.itemsContainer)}>
          {children}
        </div>
      );
      return (
        <div ref={el => this.mCarousel = el} className={outerClass} onTouchStart={this.swipe}>
          {title && <h2 className={css.heading}>{title}</h2>}
          <div className={css.arrowsContainer}>
            {buttonsOutsideItemsViewport
              ? <div className={cn(css.itemsViewport, classes.itemsViewport)}>{itemsContainerMarkup}</div>
              : itemsContainerMarkup
            }
            {this.makeArrows()}
          </div>
          {this.makeDots()}
        </div>
      );
    }
    return null;
  }
}

MelodyCarousel.snap = {
  none: carousel => {
    const {
      state: { itemsContainerWidth, rightEdgeOfLastItem },
      itemsContainer
    } = carousel;
    const maxLeft = Math.max(rightEdgeOfLastItem - itemsContainerWidth, 0);
    const match = itemsContainer.style.left.match(/^(-?\d+(?:\.\d+)?)px/);
    if (match && match[1]) {
      const currentLeft = +match[1];
      itemsContainer.style.left = Math.min(0, Math.max(currentLeft, maxLeft * -1)) + 'px';
    }
  },

  page: (carousel, newIndex) => {
    const {
      state: { itemsContainerWidth, orphanCount, pageCount, pageWidth, visibleItems },
      itemsContainer
    } = carousel;

    if (typeof newIndex === 'undefined') {
      return;
    }

    const targetPage = newIndex + 1;
    let leftPos = -(newIndex * pageWidth) + 'px';

    // don't show whitespace if last page doesn't fill up all available slots
    if (targetPage >= pageCount && orphanCount) {
      leftPos = 'calc(' + leftPos + ' + ' + ((itemsContainerWidth / visibleItems) * orphanCount) + 'px)';
    }

    itemsContainer.style.left = leftPos;

    // let css's default rules handle first page
    if (targetPage === 1) {
      itemsContainer.style.left = '';
    }
  }
};

MelodyCarousel.defaultProps = {
  buttonsOutsideItemsViewport: false,
  classes: {
    itemsViewport: '',
    prevButton: '',
    nextButton: ''
  },
  padScrollArea: true,
  showDots: true,
  snap: MelodyCarousel.snap.page
};

MelodyCarousel.propTypes = {
  buttonsOutsideItemsViewport: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  classes: PropTypes.shape({
    itemsViewport: PropTypes.string,
    prevButton: PropTypes.string,
    nextButton: PropTypes.string
  }),
  padScrollArea: PropTypes.bool,
  showDots: PropTypes.bool,
  snap: PropTypes.func,
  title: PropTypes.string
};

export default MelodyCarousel;
